/**
 * App-shell-level video panel — mounted once in ProtectedShell (outside
 * <Outlet />) so it survives navigating anywhere in the portal.
 *
 * Three presentations, ONE mounted call:
 *
 *   - Floating:  docked to the right edge, full viewport height (the original).
 *   - Minimized: a slim rail peeking out from the right edge (the original).
 *   - Docked:    the same call relocated into the consultation room's
 *                prescription rail (v2), at the top, above the Rx sections.
 *
 * ── Why the call never remounts ─────────────────────────────────────────────
 * A remount drops a live consultation, so the mechanism is deliberate and worth
 * reading before changing anything here.
 *
 * 1. `host` is ONE `<div>` created with `document.createElement` on this
 *    component's first render and never replaced. Everything about the call —
 *    `<LiveKitRoom>`, the `<VideoTrack>`s, the audio renderer — is rendered into
 *    it with a single `createPortal(..., host)` call that is present on EVERY
 *    render, in the same position of the element tree, with the same container.
 *    React reconciles a portal by comparing `containerInfo`; keeping the
 *    container identical is what makes the portal (and therefore the whole call
 *    subtree) an update rather than a delete-and-recreate. Conditionally
 *    portalling — `docked ? createPortal(x) : x` — would NOT work: a portal and
 *    a plain element are different fiber types, so React would unmount the call
 *    every time the doctor entered or left the room.
 *
 * 2. Moving between presentations moves the *DOM node*, not the React tree: an
 *    effect calls `parent.appendChild(host)`. `appendChild` on an already-parented
 *    node is an atomic move, so the `<video>` elements inside are never removed
 *    from the document — which is exactly the condition HTMLMediaElement's
 *    "pause on removal" step checks, so playback and the attached MediaStream
 *    survive the move. React never sees any of this; from its side nothing
 *    changed.
 *
 * 3. `<CallVideoArea />` sits at a FIXED position in the tree in all three
 *    presentations. Minimized hides its wrapper with a class instead of
 *    unmounting it, so even collapsing and restoring the panel doesn't recreate
 *    a video element. Only the chrome around it varies.
 *
 * The consequence to preserve: no code path between here and `<LiveKitRoom>` may
 * become conditional on presentation, and `host` must never be re-created.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock,
  PhoneOff,
  VideoOff,
} from 'lucide-react';
import {
  ControlBar,
  LiveKitRoom,
  RoomAudioRenderer,
  VideoTrack,
  useRoomContext,
  useTracks,
  type TrackReferenceOrPlaceholder,
} from '@livekit/components-react';
import { AudioPresets, RoomEvent, Track, VideoPreset, type RoomOptions } from 'livekit-client';
import '@livekit/components-styles';
import { ApiError } from '../../lib/api';
import { getDoctorId } from '../../lib/auth';
import { useCallSession, type CallPresentation } from '../../lib/call/CallSessionContext';
import { useCallQuality, type CallQualityState } from '../../lib/call/call-quality';
import { reportCallEvent } from '../../lib/call/callQualityApi';
import { AudioOnlyButton, CallStatusBanner, SignalIndicator } from './CallQualityUI';

// Per-doctor remembered device choice — kept in sync with
// ConsultationVideoLayout's original implementation (see that file's history).
const cameraStorageKey = (doctorId: string) => `kyros_dr_cam_${doctorId}`;
const micStorageKey = (doctorId: string) => `kyros_dr_mic_${doctorId}`;

// LiveKit publish/connect options tuned for Indian cellular uplinks — identical
// to the settings ConsultationVideoLayout used before this call moved out into
// the floating panel, and kept in sync with the mobile client
// (mobile/lib/call/livekit.ts).
function buildRoomOptions(doctorId: string | null): RoomOptions {
  const preferredCamera = doctorId ? localStorage.getItem(cameraStorageKey(doctorId)) : null;
  const preferredMic = doctorId ? localStorage.getItem(micStorageKey(doctorId)) : null;
  return {
    // A Retina (2x) screen shows the docked video at twice its CSS size, but
    // LiveKit only counts pixel density above 2x, so the doctor was sent the
    // 360p layer and saw it upscaled. 'screen' sizes the request in device pixels.
    adaptiveStream: { pixelDensity: 'screen' },
    dynacast: true,
    videoCaptureDefaults: preferredCamera ? { deviceId: preferredCamera } : undefined,
    audioCaptureDefaults: preferredMic ? { deviceId: preferredMic } : undefined,
    publishDefaults: {
      // Speech, not the SDK's music default: half the bitrate, and voice is all
      // a consultation needs from the microphone.
      audioPreset: AudioPresets.speech,
      simulcast: true,
      videoEncoding: { maxBitrate: 2_500_000, maxFramerate: 30 },
      videoSimulcastLayers: [
        new VideoPreset(320, 180, 200_000, 15),
        new VideoPreset(640, 360, 800_000, 25),
      ],
    },
    reconnectPolicy: {
      nextRetryDelayInMs: (ctx: { retryCount: number }) =>
        ctx.retryCount > 20 ? null : Math.min(1000 * ctx.retryCount, 7000),
    },
  };
}

function DevicePreferenceSync({ doctorId }: { doctorId: string | null }) {
  const room = useRoomContext();

  useEffect(() => {
    if (!doctorId) return;
    const handleDeviceChange = (kind: MediaDeviceKind, deviceId: string) => {
      if (kind === 'videoinput') localStorage.setItem(cameraStorageKey(doctorId), deviceId);
      else if (kind === 'audioinput') localStorage.setItem(micStorageKey(doctorId), deviceId);
    };
    room.on(RoomEvent.ActiveDeviceChanged, handleDeviceChange);
    return () => {
      room.off(RoomEvent.ActiveDeviceChanged, handleDeviceChange);
    };
  }, [room, doctorId]);

  return null;
}

function ElapsedTimer({ startISO }: { startISO: string }) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const start = new Date(startISO).getTime();
    const tick = () => setElapsed(Math.floor((Date.now() - start) / 1000));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startISO]);

  const mm = String(Math.floor(elapsed / 60)).padStart(2, '0');
  const ss = String(elapsed % 60).padStart(2, '0');

  return (
    <span className="inline-flex items-center gap-1.5 font-body text-caption text-ivory/70">
      <Clock size={12} />
      {mm}:{ss}
    </span>
  );
}

const DEFAULT_ASPECT_RATIO = '16 / 9';

/** The patient mobile app always publishes 16:9 layers (180p/360p/720p — see
 *  mobile/lib/call/livekit.ts), so 16:9 is a correct default from the first
 *  frame. Once LiveKit reports the real dimensions of whichever layer is
 *  actually subscribed, prefer that — it's what the doctor's box should be
 *  sized to, and it self-corrects if the publish config ever changes on
 *  either end instead of silently drifting out of sync. */
function useAspectRatio(trackRef: TrackReferenceOrPlaceholder | undefined): string {
  const dims = trackRef?.publication?.dimensions;
  return useMemo(
    () => (dims && dims.width > 0 && dims.height > 0 ? `${dims.width} / ${dims.height}` : DEFAULT_ASPECT_RATIO),
    [dims],
  );
}

/** Patient fills the main tile, doctor self-view is a small PIP in the corner —
 *  same behavior as the pre-refactor ConsultationVideoLayout, just without the
 *  click-to-swap (there's no room for a second full tile in a 420px-wide
 *  panel; the doctor's own view is a fixed corner PIP here).
 *
 *  This component's position in the element tree is identical in all three
 *  presentations. That is load-bearing — see the file header. */
function noVideoMessage(quality: CallQualityState): string {
  if (quality.level === 3) return 'Audio only — video is paused to keep the voice clear.';
  if (quality.peerAudioOnly) return 'Patient is on audio only.';
  return 'Patient’s camera is off.';
}

function CallVideoArea({ fill, quality }: { fill: boolean; quality: CallQualityState }) {
  const tracks = useTracks(
    [{ source: Track.Source.Camera, withPlaceholder: true }],
    { onlySubscribed: false },
  );

  const localTrack = tracks.find(t => t.participant.isLocal);
  const remoteTrack = tracks.find(t => !t.participant.isLocal);
  const mainTrack = remoteTrack ?? localTrack;
  const aspectRatio = useAspectRatio(mainTrack);

  // `fill` is the docked, expanded case: the tile takes the rail's whole
  // remaining height instead of being an aspect-sized box, so the patient is
  // the full height of the page rather than a strip at the top. objectFit
  // stays `contain`, so a feed whose shape doesn't match the rail letterboxes
  // rather than cropping the patient's face out of frame.
  //
  // Sizing the element up is not only cosmetic: `adaptiveStream` picks which
  // simulcast layer to subscribe to from the element's rendered size, so a
  // taller tile is also a sharper one.
  // Nothing is mirrored — the doctor's own video shows the true view, like the
  // patient's (product decision, 8 Oct 2026). `transform: 'none'` also overrides
  // LiveKit's stylesheet, which flips local camera video in some layouts.
  return (
    <div
      className={`w-full bg-ink relative ${fill ? 'h-full' : ''}`}
      style={fill ? undefined : { aspectRatio }}
    >
      {mainTrack?.publication?.track ? (
        <VideoTrack
          trackRef={mainTrack}
          className="w-full h-full"
          style={{
            objectFit: 'contain',
            transform: 'none',
          }}
        />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center gap-1.5 px-4 text-center">
          <VideoOff size={28} className="text-stone" />
          {remoteTrack && (
            <p className="font-body text-caption text-ivory/70">{noVideoMessage(quality)}</p>
          )}
        </div>
      )}

      {/* Doctor self-view PIP — only shown once a remote participant occupies
          the main tile; with no remote yet the local feed already IS the main
          tile above, so a second copy would be redundant. */}
      {remoteTrack && localTrack?.publication?.track && (
        <div className="absolute bottom-2 right-2 w-[28%] min-w-[4rem] max-w-[7rem] aspect-[3/2] rounded-md overflow-hidden border-2 border-ivory/30 shadow-lg">
          <VideoTrack
            trackRef={localTrack}
            className="w-full h-full"
            style={{ objectFit: 'cover', transform: 'none' }}
          />
        </div>
      )}
    </div>
  );
}

interface CallShellProps {
  consultationId: string;
  presentation: CallPresentation;
  patientName: string;
  scheduledStartAt: string;
  onMinimize: () => void;
  onMaximize: () => void;
}

/**
 * Leaving is the one control in this bar that cannot be undone by clicking it
 * again — the patient is dropped, and rejoining means a fresh consent and a
 * fresh token. LiveKit's own leave button disconnects on the first click, next
 * to a camera toggle that is harmless, so it asks first.
 *
 * `room.disconnect()` goes through `onDisconnected` exactly as the built-in
 * button does, so ending still runs the one `handleEnd` path.
 */
function LeaveButton({ compact }: { compact: boolean }) {
  const room = useRoomContext();
  const [confirming, setConfirming] = useState(false);

  if (!confirming) {
    return (
      <button
        type="button"
        onClick={() => setConfirming(true)}
        aria-label="Leave call"
        className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-alert/90 px-2.5 py-1.5 font-body text-caption font-semibold text-white transition-colors hover:bg-alert"
      >
        <PhoneOff size={14} />
        {!compact && 'Leave'}
      </button>
    );
  }

  return (
    <div
      role="alertdialog"
      aria-label="Leave this call?"
      className="flex shrink-0 items-center gap-1.5 rounded-md bg-ink px-2 py-1.5"
    >
      <span className="font-body text-caption text-ivory">Leave call?</span>
      <button
        type="button"
        onClick={() => setConfirming(false)}
        className="rounded-md px-2 py-1 font-body text-caption font-medium text-ivory/80 transition-colors hover:bg-white/10 hover:text-ivory"
      >
        Cancel
      </button>
      <button
        type="button"
        autoFocus
        onClick={() => void room.disconnect()}
        className="rounded-md bg-alert px-2 py-1 font-body text-caption font-semibold text-white transition-colors hover:bg-alert/90"
      >
        Leave
      </button>
    </div>
  );
}

/**
 * All three presentations, one element tree.
 *
 * Chrome varies by className and by which header is rendered; the video area and
 * the control bar are always in the same slot, and minimized hides them rather
 * than removing them. Restructuring this into per-presentation components would
 * reintroduce exactly the remount this file exists to avoid.
 */
function CallShell({
  consultationId,
  presentation,
  patientName,
  scheduledStartAt,
  onMinimize,
  onMaximize,
}: CallShellProps) {
  const qc = useQueryClient();
  const room = useRoomContext();
  // CallShell stays mounted for the whole call in every presentation, so the
  // quality ladder (lib/call/call-quality.ts) lives here.
  const { state: quality, goAudioOnly, turnVideoBackOn } = useCallQuality(room, {
    peerIdentity: 'patient',
    report: (event, trigger) => {
      reportCallEvent(consultationId, event, trigger).catch(() => undefined);
    },
    // Both cameras live together for 30 s. Recorded once on the consultation;
    // a first visit without it gets the List A warning in the prescription rail.
    // The returned promise tells the controller whether it was recorded: a
    // network or server error rejects, so it is tried again; a definite answer
    // (e.g. 409, the visit is no longer in progress) does not.
    onVideoEstablished: () =>
      reportCallEvent(consultationId, 'video_established')
        .then(() => qc.invalidateQueries({ queryKey: ['consultation', consultationId] }))
        .catch((err: unknown) => {
          if (err instanceof ApiError && err.status < 500 && err.status !== 429) return;
          throw err;
        }),
  });
  const audioOnly = quality.level === 3;
  const minimized = presentation === 'minimized';
  const dockedCollapsed = presentation === 'docked-collapsed';
  const docked = presentation === 'docked' || dockedCollapsed;
  // Collapsed, in either shell, hides the same two slots. It never unmounts
  // them — see the note above each one.
  const bodyHidden = minimized || dockedCollapsed;

  // Docked and expanded is a full-height column now, so the video tile can take
  // every pixel the rail isn't using for chrome. Collapsed still sizes to its
  // own content — it IS just the bar.
  return (
    <div className={dockedCollapsed ? 'flex flex-col' : 'flex flex-col h-full'}>
      {minimized ? (
        <button
          onClick={onMaximize}
          title={`Resume video call with ${patientName}`}
          aria-label="Expand video call"
          className="flex flex-col items-center gap-3 w-full h-full pt-4 pb-3 bg-ink hover:bg-ink/90 transition-colors"
        >
          <ChevronLeft size={18} className="text-ivory/80" />
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-alert opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-alert" />
          </span>
          <ElapsedTimer startISO={scheduledStartAt} />
        </button>
      ) : docked ? (
        <div
          className={`flex items-center justify-between gap-2 px-2.5 py-1.5 bg-ink shrink-0 ${
            dockedCollapsed ? 'rounded-lg' : 'rounded-t-lg'
          }`}
        >
          <div className="flex min-w-0 items-center gap-2">
            {/* Collapsed, this bar is the only evidence the call is still up —
                the timer alone reads as a stopwatch, not as "you are live". */}
            {dockedCollapsed && (
              <span className="relative flex h-2 w-2 shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-alert opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-alert" />
              </span>
            )}
            <p className="font-body text-caption font-medium text-ivory truncate">{patientName}</p>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {/* The consultation timer stays visible for the whole call — slot-based
                pricing means elapsed time is information the doctor acts on. */}
            <SignalIndicator signal={quality.signal} />
            <ElapsedTimer startISO={scheduledStartAt} />
            <button
              onClick={dockedCollapsed ? onMaximize : onMinimize}
              title={dockedCollapsed ? 'Show video' : 'Collapse video'}
              aria-label={dockedCollapsed ? 'Show video call' : 'Collapse video call'}
              aria-expanded={!dockedCollapsed}
              className="rounded-md p-1 text-ivory/70 transition-colors hover:bg-white/10 hover:text-ivory"
            >
              {dockedCollapsed ? <ChevronDown size={15} /> : <ChevronUp size={15} />}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between px-3 py-2.5 bg-ink shrink-0">
          <div className="min-w-0">
            <p className="font-body text-caption font-medium text-ivory truncate">{patientName}</p>
            <span className="inline-flex items-center gap-2">
              <ElapsedTimer startISO={scheduledStartAt} />
              <SignalIndicator signal={quality.signal} />
            </span>
          </div>
          <button
            onClick={onMinimize}
            title="Minimize"
            aria-label="Minimize video call"
            className="shrink-0 p-1.5 rounded-md text-ivory/70 hover:text-ivory hover:bg-white/10 transition-colors"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      )}

      {/* Hidden, not unmounted, when collapsed — in the rail exactly as in the
          floating shell. Unmounting would detach the video track and make
          collapsing a way to drop the call. */}
      <div
        className={
          bodyHidden ? 'hidden' : docked ? 'min-h-0 flex-1 overflow-hidden' : 'min-h-0 flex-1'
        }
      >
        <CallVideoArea fill={!minimized} quality={quality} />
      </div>

      {!bodyHidden && <CallStatusBanner state={quality} onTurnVideoBackOn={turnVideoBackOn} />}

      {/* The bar wraps rather than overflowing. At rail widths the three
          controls plus their device pickers are wider than the column, and
          without this the Leave button was clipped off the right edge — the
          one control that must always be reachable. */}
      <div
        className={
          bodyHidden
            ? 'hidden'
            : docked
              ? 'shrink-0 rounded-b-lg overflow-hidden'
              : 'mt-auto shrink-0'
        }
      >
        <div className="flex flex-wrap items-center justify-center gap-1.5 bg-ink px-1.5 py-1.5">
          {/* No camera toggle on audio only: the camera comes back through
              "Turn video back on", which also restores the patient's video. */}
          <ControlBar
            controls={{ screenShare: false, chat: false, leave: false, camera: !audioOnly }}
            className="!bg-ink !border-t-0 !p-0 !flex-wrap !justify-center !gap-1.5"
          />
          <AudioOnlyButton audioOnly={audioOnly} onClick={audioOnly ? turnVideoBackOn : goAudioOnly} />
          <LeaveButton compact={docked} />
        </div>
      </div>
    </div>
  );
}

export function FloatingConsultationPanel() {
  const { session, dockTarget, presentation, endCall, minimize, maximize } = useCallSession();
  const qc = useQueryClient();
  const [doctorId] = useState(() => getDoctorId());
  const roomOptions = useMemo(() => buildRoomOptions(doctorId), [doctorId]);

  const floatingRef = useRef<HTMLDivElement | null>(null);
  // The one node the call lives in. `useState` with an initializer, not
  // `useMemo`: React may discard and recompute a useMemo value, and recreating
  // this div would recreate the entire call.
  const [host] = useState(() => {
    const el = document.createElement('div');
    el.style.width = '100%';
    el.style.height = '100%';
    return el;
  });

  const docked = dockTarget !== null;

  // Relocate the host node between the floating shell and the room's rail. This
  // is the ONLY thing that changes when the doctor enters or leaves the room.
  useEffect(() => {
    if (!session) return;
    const parent = dockTarget ?? floatingRef.current;
    if (!parent || host.parentNode === parent) return;
    // Atomic move: the video elements inside are never out of the document, so
    // nothing pauses and no track is re-attached.
    parent.appendChild(host);
  }, [dockTarget, host, session]);

  // The floating shell wants the call to fill its height; the rail wants it to
  // size to its own content. A style change, never a structural one.
  // Collapsed is the only presentation that sizes to its own content; the
  // expanded dock now fills the rail slot exactly as the floating shell fills
  // its own, which is what lets the video be full height.
  const collapsedInRail = presentation === 'docked-collapsed';
  useEffect(() => {
    host.style.height = collapsedInRail ? 'auto' : '100%';
  }, [host, collapsedInRail]);

  if (!session) return null;

  const handleEnd = () => {
    const { consultationId } = session;
    endCall();
    // Refresh the consultation query so the page reflects the call having
    // ended if/when the doctor is back on it — completing the consultation
    // record is still a separate, explicit action (CompleteConsultationButton).
    qc.invalidateQueries({ queryKey: ['consultation', consultationId] });
  };

  return (
    <>
      {/* The floating shell. Still rendered while docked (so undocking has
          somewhere to put the call back) but zero-width and inert — the call's
          DOM node is not inside it at that moment. */}
      <div
        ref={floatingRef}
        aria-hidden={docked}
        className={
          docked
            ? 'fixed top-0 right-0 h-0 w-0 overflow-hidden pointer-events-none'
            : `fixed top-0 right-0 h-screen z-50 shadow-2xl transition-[width] duration-300 ease-in-out ${
                session.minimized ? 'w-14' : 'w-[420px]'
              }`
        }
      />

      {createPortal(
        <LiveKitRoom
          serverUrl={session.joinResp.endpoint}
          token={session.joinResp.token}
          connect
          video
          audio
          options={roomOptions}
          onDisconnected={handleEnd}
          // A failed connect (bad/expired token, WS blocked, LiveKit unreachable)
          // is treated the same as a normal disconnect: end the session so the
          // consultation page's consent gate reappears for a retry, rather than
          // leaving a panel mounted with a connection that never joined.
          onError={handleEnd}
          data-lk-theme="default"
          style={{ height: collapsedInRail ? 'auto' : '100%' }}
        >
          {/* Unconditional, in every presentation. Audio in particular: these
              used to sit inside the maximized branch, which silently dropped
              the patient's audio whenever the doctor minimized the panel —
              contrary to what this file's own header claimed. Hoisting them is
              the fix, and it is also what makes the call survive presentation
              changes. */}
          <RoomAudioRenderer />
          <DevicePreferenceSync doctorId={doctorId} />

          <CallShell
            consultationId={session.consultationId}
            presentation={presentation}
            patientName={session.patientName}
            scheduledStartAt={session.scheduledStartAt}
            onMinimize={minimize}
            onMaximize={maximize}
          />
        </LiveKitRoom>,
        host,
      )}
    </>
  );
}
